/**
 * Date utility functions for formatting and handling dates across the application
 */

/**
 * Format a date range for display
 * @param startDate - Start date string or null
 * @param endDate - End date string or null
 * @param options - Formatting options
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  options: {
    fallback?: string;
    format?: 'short' | 'long' | 'numeric';
  } = {}
): string => {
  const { fallback = 'Not specified', format = 'short' } = options;

  if (!startDate || !endDate) {
    return fallback;
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return fallback;
    }

    let dateFormatOptions: Intl.DateTimeFormatOptions;
    
    switch (format) {
      case 'long':
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        };
        break;
      case 'numeric':
        dateFormatOptions = { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        };
        break;
      case 'short':
      default:
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        };
        break;
    }

    const startFormatted = start.toLocaleDateString(undefined, dateFormatOptions);
    const endFormatted = end.toLocaleDateString(undefined, dateFormatOptions);

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    console.warn('Error formatting date range:', error);
    return fallback;
  }
};

/**
 * Format a single date for display
 * @param date - Date string or null
 * @param options - Formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | null | undefined,
  options: {
    fallback?: string;
    format?: 'short' | 'long' | 'numeric';
    includeTime?: boolean;
  } = {}
): string => {
  const { fallback = 'N/A', format = 'short', includeTime = false } = options;

  if (!date) {
    return fallback;
  }

  try {
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }

    let dateFormatOptions: Intl.DateTimeFormatOptions;
    
    switch (format) {
      case 'long':
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        };
        break;
      case 'numeric':
        dateFormatOptions = { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        };
        break;
      case 'short':
      default:
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        };
        break;
    }

    if (includeTime) {
      dateFormatOptions = {
        ...dateFormatOptions,
        hour: '2-digit',
        minute: '2-digit'
      };
    }

    return dateObj.toLocaleDateString(undefined, dateFormatOptions);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Calculate the number of days between two dates
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Number of days or null if invalid dates
 */
export const calculateDaysBetween = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number | null => {
  if (!startDate || !endDate) {
    return null;
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return null;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.warn('Error calculating days between dates:', error);
    return null;
  }
};
